import { useMemo, useState, useRef } from "react";
import { ShieldCheck, DownloadSimple } from "@phosphor-icons/react";
import { authApi } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const AGREEMENT_VERSION = "1.0";

const SECTIONS = [
    {
        heading: "1. ELIGIBILITY",
        body: "You confirm you are authorised to represent your organisation and enter into agreements on its behalf.",
    },
    {
        heading: "2. JOB POSTINGS",
        body: "All job postings must be for genuine, currently open roles. Misrepresentative, discriminatory, or fraudulent postings are prohibited. The Platform reserves the right to remove any posting without notice.",
    },
    {
        heading: "3. CANDIDATE DATA — DPDP ACT 2023",
        body: "Candidate personal data (resumes, screening responses, audio recordings, video submissions) is shared with you solely for evaluating candidates for roles you have posted. You must not: (a) share candidate data with third parties, (b) use it for purposes other than recruitment, (c) retain it beyond 90 days of the candidate's last application. You are a Data Fiduciary under the DPDP Act 2023 for data you process.",
    },
    {
        heading: "4. SCREENING AND AI TOOLS",
        body: "AI-generated JDs and screening rubrics are tools to assist your hiring. Final hiring decisions rest with you. You must not use AI outputs as the sole basis for rejection in a manner that constitutes unlawful discrimination.",
    },
    {
        heading: "5. INTERVIEW CONDUCT",
        body: "All interviews must be conducted professionally and in compliance with applicable Indian labour laws. Candidates must not be subjected to discriminatory questioning.",
    },
    {
        heading: "6. CONFIDENTIALITY",
        body: "Platform features, pricing, and candidate data are confidential and must not be disclosed to third parties.",
    },
    {
        heading: "7. GOVERNING LAW",
        body: "This agreement is governed by the laws of India. Disputes are subject to jurisdiction of courts in India.",
    },
];

function agreementAsText() {
    let body = `Employer Platform Agreement — BookMyInterview.ai\nVersion ${AGREEMENT_VERSION}\n\n`;
    body += `By accepting this agreement, you ("Employer") agree to the following terms governing your use of the BookMyInterview.ai platform ("Platform").\n\n`;
    for (const s of SECTIONS) {
        body += `${s.heading}\n${s.body}\n\n`;
    }
    body += `By clicking "I Agree", you confirm you have read and agree to this agreement on behalf of your organisation.\n\n`;
    body += `Downloaded at: ${new Date().toISOString()}\n`;
    return body;
}

export default function EmployerAgreementModal() {
    const { employer, refreshMe } = useAuth();
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);
    const [scrolled, setScrolled] = useState(false);

    // Show only when: employer loaded, verified, no agreement accepted yet, and NOT team-member session.
    const shouldShow = useMemo(() => {
        if (!employer || employer === false) return false;
        if (employer.team_member_id) return false;   // team members inherit acceptance
        // RM delegated session (2026-06) — the platform agreement is the
        // CLIENT admin's to accept, never the RM's. Don't block the
        // delegated workspace with it.
        if (employer.is_rm_session) return false;
        if (employer.role === "auditor" || employer.role === "admin") return false;
        const acc = employer.agreement_accepted;
        return !acc || !acc.accepted_at;
    }, [employer]);

    if (!shouldShow) return null;

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        // Consider "read" once user scrolls within 40px of the bottom.
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
            setScrolled(true);
        }
    };

    const accept = async () => {
        setBusy(true);
        try {
            await authApi.acceptAgreement(AGREEMENT_VERSION);
            await refreshMe();
            toast.success("Agreement accepted. Welcome aboard.");
        } catch (err) {
            toast.error("Couldn't record acceptance. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const downloadCopy = () => {
        try {
            const text = agreementAsText();
            const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `bookmyinterview-employer-agreement-v${AGREEMENT_VERSION}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed.");
        }
    };

    return (
        <div
            data-testid="employer-agreement-modal"
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agreement-title"
        >
            <div className="bg-background border border-border rounded-lg shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col">
                <div className="flex items-start justify-between p-6 border-b border-border">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-1 inline-flex items-center gap-1.5">
                            <ShieldCheck size={13} weight="fill" /> Legal · India
                        </div>
                        <h2 id="agreement-title" className="font-serif text-2xl leading-tight">
                            Employer Platform Agreement — BookMyInterview.ai
                        </h2>
                        <div className="text-xs text-muted-foreground mt-1">Version {AGREEMENT_VERSION}</div>
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    onScroll={onScroll}
                    data-testid="employer-agreement-scroll"
                    className="flex-1 overflow-y-auto p-6 space-y-5 text-sm text-foreground/85 leading-relaxed"
                >
                    <p>
                        By accepting this agreement, you (&quot;Employer&quot;) agree to the following terms governing your use of the BookMyInterview.ai platform (&quot;Platform&quot;).
                    </p>
                    {SECTIONS.map((s) => (
                        <div key={s.heading}>
                            <h3 className="font-semibold text-foreground mb-1.5">{s.heading}</h3>
                            <p>{s.body}</p>
                        </div>
                    ))}
                    <p className="text-foreground/70 italic pt-2">
                        By clicking &quot;I Agree&quot;, you confirm you have read and agree to this agreement on behalf of your organisation.
                    </p>
                </div>

                <div className="p-6 border-t border-border bg-muted/30 flex items-center justify-between gap-4 flex-wrap">
                    <button
                        type="button"
                        data-testid="employer-agreement-download-btn"
                        onClick={downloadCopy}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                    >
                        <DownloadSimple size={13} weight="bold" /> Download a copy
                    </button>
                    <button
                        type="button"
                        data-testid="employer-agreement-accept-btn"
                        onClick={accept}
                        disabled={busy}
                        className="bg-primary text-primary-foreground text-sm font-medium px-5 py-2.5 rounded-sm hover:-translate-y-[1px] hover:shadow-md transition-all disabled:opacity-60 disabled:hover:translate-y-0"
                    >
                        {busy ? "Saving..." : "I Agree — Activate My Account"}
                    </button>
                </div>
            </div>
        </div>
    );
}
